import React, {ReactNode, createContext, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {EstimateTreeDto, TreeAdditionalDetails, TreeMaterialSpec, WorkItemType} from '../../../shared/dtos/estimate-tree.dto';
import {EstimateTreeOperationDto, TreeOperationType} from '../../../shared/dtos/estimate-tree-operation.dto';
import {EstimateGateDetailsDto} from '../../../shared/dtos/estimate-gate.dto';
import {useUserSettings} from '../../../components/UserSettingsContext';
import {compressImage} from '../../../shared/misc/image-helpers';
import {isAndroidDevice, isIosDevice} from '../../../shared/misc/dom-utils';
import {getCustomToken} from '../../../shared/services/auth.service';
import {AreaEntry, areaDetailPatches, areaEntries, areaSpecFor} from '../operation-areas.service';
import {SPECIES_LIST} from '../estimate-tree-detail/tree-species';
import {equipmentName, formatMinutes, money, rowCost} from './record-math';
import {
  COUNT_OPTIONS, deleteWholeLabel, FEET_OPTIONS, hasSelectionList, INCH_OPTIONS, OperationField, operationFields, operationLabel, operationsFor,
  storedAnswer, stumpGrindingSeedNote, totalLabel, TREE_COUNT_OPTIONS,
} from './operation-schema';
import {hideGatePhotoConfirm, Photo, PhotoPager, photosOf} from './PhotoPager';
import {AiStatus, crewCaption, detail, MinimumAppliedTag, minimumApplied, RecordContext, SiteLine, workItemTitle} from './WorkItemCard';
import {
  ANY_LENGTH, appendKey, AREA_LABELS, brushWoodComplete, brushWoodLines, brushWoodOptions, CLEAN_HAUL, CLEANUP_FOLLOW_UP, CLEANUP_OPTIONS,
  cleanupComplete, cleanupDetail, cleanupPatch, CUT_TO_LENGTH, DROPPING_OPTIONS, droppingAnswer, FELL_WHOLE_TREE, HAULED_AWAY, isCompactLoader, isStumpWork, LEAVE_BRUSH_HAUL_WOOD,
  LEAVE_EVERYTHING, LEAVE_WOOD_HAUL_BRUSH, LOADER_ACCESS_OPTIONS, loaderAccessAnswer, loaderMaterialAnswer, loaderMaterialLabel, loaderMaterialOptions, pickedInOrder,
  planMachines, planSections, REACH_OPTIONS, REMAINING_STEM_OPTIONS, REMOVAL_OPTIONS, removeKey, RIGGING_OPTIONS, riggingAnswer, SPECIFIC_LOCATION,
  specOf, storedOrder, WHERE_IT_FALLS,
} from './work-plan';
import {WorkItems} from './useWorkItems';
import {machineRanLine} from './job-math';
import {missingPriceReason, parentStepRefusal} from './parent-adjust';
import {useTimedNotice} from './timed-notice';
import './WorkItemEdit.css';

/*
 * One work item in edit mode: Android's WorkItemCard unlocked
 * (sections/estimate/WorkItemsView.kt, OperationConfigFields.kt,
 * OperationAreaFields.kt, WorkPlanBlock.kt). Every change goes through the
 * page's work items (useWorkItems), which save it the way Android does. The
 * wide-card rules (SQ-596) are in WorkItemEdit.css: buttons and answers keep
 * their phone size and fill from the left, the photo stops at 480, and the
 * card keeps its view mode's lines (three measurements a row at every width).
 */

// ---- Small pieces ---------------------------------------------------------------------------

/** A note field shows all its lines (Android's fields grow): the CSS sizes it to its text where it can, this is the fallback. */
const noteRows = (text?: string | null) => Math.min(12, Math.max(2, (text ?? '').split('\n').length + 1));

/** Holding a ± repeats it every 90 ms after a moment, as Android's stepper does. */
const useHoldRepeat = (onStep: () => void) => {
  const timer = useRef<number>();
  const stepRef = useRef(onStep);
  stepRef.current = onStep;
  const stop = () => window.clearTimeout(timer.current);
  useEffect(() => stop, []);
  return {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      stepRef.current();
      const repeat = () => { stepRef.current(); timer.current = window.setTimeout(repeat, 90); };
      timer.current = window.setTimeout(repeat, 400);
    },
    onPointerUp: stop, onPointerLeave: stop, onPointerCancel: stop,
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); stepRef.current(); } },
  };
};

const StepButton = ({label, glyph, onStep, disabled}: {label: string; glyph: string; onStep: () => void; disabled?: boolean}) => {
  const hold = useHoldRepeat(() => { if (!disabled) onStep(); });
  return <button type="button" aria-label={label} aria-disabled={disabled} {...hold}>{glyph}</button>;
};

/**
 * CompactTimerPill: "− HR +" | "− MIN +", centred, the caption under it.
 * With no crew picked the buttons fade and do nothing, and the red line
 * says why. Equipment pills carry no caption.
 *
 * SQ-660 (TimerPillWithCaption): with no crew, a tap on the faded pill or
 * the red line opens the crew picker (`onSelectCrew`). A parent timer also
 * fades while `blockedReason` says an operation has no price, and a tap
 * shows that reason under it for a few seconds; `stepRefusal` does the same
 * for a step that would take an operation to $0, and the step is not made.
 */
export const TimerPill = ({onStep, caption, crewSelected = true, target, onSelectCrew, blockedReason, stepRefusal}: {
  onStep: (deltaMinutes: number) => void; caption?: string; crewSelected?: boolean; target?: string;
  onSelectCrew?: () => void; blockedReason?: string; stepRefusal?: (deltaMinutes: number) => string | undefined;
}) => {
  const [notice, setNotice] = useTimedNotice();
  // A reason that no longer holds (the crew was picked, the operation priced) goes at once.
  useEffect(() => setNotice(undefined), [blockedReason, crewSelected, setNotice]);
  const enabled = crewSelected && !blockedReason;
  const step = (delta: number) => {
    const refusal = stepRefusal?.(delta);
    if (refusal) setNotice(refusal); else onStep(delta);
  };
  // The faded pill's buttons do nothing; a tap on any of it says why, or asks for crew.
  const blockedTap = enabled ? undefined : () => { if (!crewSelected) onSelectCrew?.(); else setNotice(blockedReason); };
  return (
    <div className="we-timer">
      <div className={enabled ? 'we-timer-pill' : 'we-timer-pill we-timer-pill--off'} role="group" aria-label={target ? `${target} time` : 'Time'}
           onClick={blockedTap}>
        <StepButton label="Less, an hour" glyph="−" disabled={!enabled} onStep={() => step(-60)}/><span>HR</span>
        <StepButton label="More, an hour" glyph="+" disabled={!enabled} onStep={() => step(60)}/>
        <span className="we-timer-divider"/>
        <StepButton label="Less, a minute" glyph="−" disabled={!enabled} onStep={() => step(-1)}/><span>MIN</span>
        <StepButton label="More, a minute" glyph="+" disabled={!enabled} onStep={() => step(1)}/>
      </div>
      {crewSelected
        ? caption && <div className="we-caption">{caption}</div>
        : onSelectCrew
          ? <button type="button" className="we-caption we-caption--error we-caption--link" onClick={onSelectCrew}>Select crew members to operate timer</button>
          : <div className="we-caption we-caption--error">Select crew members to operate timer</div>}
      {notice && <div className="we-timer-notice" role="status">{notice}</div>}
    </div>
  );
};

/** TitledSelectBox (MeasurementField.kt): the title above a white box; "—" when empty. */
const SelectBox = ({title, value, options, labels, onChange}: {
  title: string; value?: string; options: string[]; labels?: Record<string, string>; onChange: (v: string) => void;
}) => (
  <label className="we-select">
    <span className="we-select-title">{title}</span>
    <select value={value ?? ''} onChange={e => onChange(e.target.value)}>
      <option value="">—</option>
      {value && !options.includes(value) && <option value={value}>{value}</option>}
      {options.map(o => <option key={o} value={o}>{labels?.[o] ?? o.trim()}</option>)}
    </select>
  </label>
);

/** A typed field with its title above it (the details boxes: NoteField's titled form). */
const TextBox = ({title, value, placeholder, multiline, list, onChange}: {
  title: string; value?: string; placeholder?: string; multiline?: boolean; list?: string; onChange: (v: string) => void;
}) => (
  <label className="we-select">
    <span className="we-select-title">{title}</span>
    {multiline
      ? <textarea rows={noteRows(value)} value={value ?? ''} placeholder={placeholder} autoCapitalize="sentences" onChange={e => onChange(e.target.value)}/>
      : <input value={value ?? ''} placeholder={placeholder} list={list} onChange={e => onChange(e.target.value)}/>}
  </label>
);

/** ChipOptionRow: a radio circle and the answer; the outline is the picked state. */
const Chip = ({label, on, onClick}: {label: string; on: boolean; onClick: () => void}) => (
  <button type="button" className={on ? 'we-chip we-chip--on' : 'we-chip'} onClick={onClick} aria-pressed={on}>
    <span className="we-chip-radio"/>{label}
  </button>
);

/**
 * OneRowChips (WorkPlanBlock.kt, SQ-644): short answers side by side, never on a second row. With room every label is
 * on one line; without, a label breaks after its "&" and its chip narrows to the longer line (a label broken by the
 * width alone keeps the full width, and "Grindings" lost its "s" to a second line). Only a screen too narrow even
 * for the broken labels lets a chip drop to a second row. An invisible one-line copy says whether the row fits. The
 * chips are one height, across rows too.
 */
const OneRowChips = ({options, value, labels, onPick}: {
  options: string[]; value?: string; labels?: Record<string, string>; onPick: (v: string) => void;
}) => {
  const row = useRef<HTMLDivElement>(null);
  const probe = useRef<HTMLDivElement>(null);
  const [broken, setBroken] = useState(false);
  const shown = options.map(o => labels?.[o] ?? o).join('|');
  useLayoutEffect(() => {
    const check = () => {
      if (row.current && probe.current) setBroken(probe.current.scrollWidth > row.current.clientWidth);
    };
    check();
    if (typeof ResizeObserver === 'undefined' || !row.current) return;
    const observer = new ResizeObserver(check);
    observer.observe(row.current);
    return () => observer.disconnect();
  }, [shown]);
  const chips = (breakAtAmp: boolean) => options.map(o => {
    const label = labels?.[o] ?? o;
    return <Chip key={o} label={breakAtAmp ? label.replace(' & ', ' &\n') : label} on={value === o} onClick={() => onPick(value === o ? '' : o)}/>;
  });
  return (
    <div ref={row} className={broken ? 'we-chips we-chips--row we-chips--broken' : 'we-chips we-chips--row'}>
      <div ref={probe} className="we-chips we-chips--row we-chips--probe" aria-hidden>{chips(false)}</div>
      {chips(broken)}
    </div>
  );
};

/** Pick one of the answers; tapping the picked one clears it. */
const PickOne = ({options, value, grid, row, labels, onPick}: {
  options: string[]; value?: string; grid?: boolean; row?: boolean; labels?: Record<string, string>; onPick: (v: string) => void;
}) => row ? <OneRowChips options={options} value={value} labels={labels} onPick={onPick}/> : (
  <div className={grid ? 'we-chips we-chips--grid' : 'we-chips'}>
    {options.map(o => <Chip key={o} label={labels?.[o] ?? o} on={value === o} onClick={() => onPick(value === o ? '' : o)}/>)}
  </div>
);

/** PickOneRow (OperationConfigFields.kt): open while unanswered; a pick closes it and the answer takes its place. */
const PickOneRow = ({field, value, onPick}: {field: OperationField; value?: string; onPick: (v: string) => void}) => {
  const [open, setOpen] = useState(!value);
  return (
    <div className="we-pick">
      <button type="button" className="we-pick-head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="we-pick-label">{field.label}</span>
        {!open && value && <span className="we-pick-answer">{field.optionLabels?.[value] ?? value}</span>}
        {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
      </button>
      {open && <PickOne options={field.options ?? []} value={value} labels={field.optionLabels} onPick={v => { onPick(v); setOpen(!v); }}/>}
    </div>
  );
};

/** GreenCheckbox: an 18px square, green with a white tick when checked. */
const GreenCheckbox = ({checked}: {checked: boolean}) => (
  <span className={checked ? 'we-gcheck we-gcheck--on' : 'we-gcheck'} aria-hidden="true">{checked && '✓'}</span>
);

/** AddNoteButton: "+" and the bold label; tapped, the note field with the cursor in it. */
const NoteButton = ({label, placeholder, value, crew, title, onChange}: {
  label: string; placeholder: string; value?: string | null; crew?: boolean; title?: string; onChange: (v: string) => void;
}) => {
  const [open, setOpen] = useState(!!value?.trim());
  const [focus, setFocus] = useState(false);
  if (!open) {
    return (
      <div>
        <button type="button" className={crew ? 'we-note-button we-note-button--crew' : 'we-note-button'} onClick={() => { setFocus(true); setOpen(true); }}>
          <span className="we-note-plus">+</span><b>{label}</b>
        </button>
      </div>
    );
  }
  const field = (
    <textarea className={crew ? 'we-note-field we-note-field--crew' : 'we-note-field'} rows={noteRows(value)} value={value ?? ''} placeholder={placeholder}
              autoFocus={focus} autoCapitalize="sentences" onChange={e => onChange(e.target.value)}/>
  );
  return title ? <label className="we-select we-note-titled"><span className="we-select-title">{title}</span>{field}</label> : field;
};

const ConfirmDialog = ({title, text, confirm, onConfirm, onClose}: {
  title: string; text: string; confirm: string; onConfirm: () => void; onClose: () => void;
}) => (
  <Dialog open onClose={onClose} maxWidth="xs">
    <DialogTitle>{title}</DialogTitle>
    <DialogContent><DialogContentText>{text}</DialogContentText></DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Keep</Button>
      <Button color="error" onClick={() => { onClose(); onConfirm(); }}>{confirm}</Button>
    </DialogActions>
  </Dialog>
);

// ---- Measurements --------------------------------------------------------------------------

/** MeasurementsBlock: three per row (1 : 1 : 1.35), dropdowns over their option lists, Species typed with suggestions. */
const Measurements = ({tree, items}: {tree: EstimateTreeDto; items: WorkItems}) => {
  const set = (patch: Partial<EstimateTreeDto>) => items.changeTree(tree.id, t => ({...t, ...patch}));
  const setDetail = (key: string, value: string) => items.setDetails(tree.id, {[key]: value} as Partial<TreeAdditionalDetails>);
  const group = tree.workItemType === WorkItemType.TreeGroup;
  const avg = group ? 'Avg. ' : '';
  const listId = `species-${tree.id}`;
  const species = <TextBox title="Species" value={tree.species} placeholder="Type or pick" list={listId} onChange={v => set({species: v})}/>;
  const height = <SelectBox title={`${avg}Height`} value={tree.height} options={FEET_OPTIONS} onChange={v => set({height: v})}/>;
  const width = <SelectBox title={`${avg}Width`} value={tree.width} options={FEET_OPTIONS} onChange={v => set({width: v})}/>;
  const trunk = <SelectBox title={`${avg}Trunk Diameter`} value={tree.trunkDiameter} options={INCH_OPTIONS} onChange={v => set({trunkDiameter: v})}/>;
  const trunks = <SelectBox title={group ? 'Avg # Trunks' : '# of Trunks'} value={detail(tree, 'Tree_trunkCount')} options={COUNT_OPTIONS}
                            onChange={v => setDetail('Tree_trunkCount', v)}/>;
  const rows: (ReactNode | null)[][] = (() => {
    switch (tree.workItemType) {
      case WorkItemType.Misc: return [];
      case WorkItemType.Stump:
        return [[<SelectBox title="Stump Diameter" value={tree.trunkDiameter} options={INCH_OPTIONS} onChange={v => set({trunkDiameter: v})}/>, null, species]];
      case WorkItemType.DebrisPile:
        return [[height, width, <SelectBox title="Length" value={detail(tree, 'DebrisPile_length')} options={FEET_OPTIONS}
                                           onChange={v => setDetail('DebrisPile_length', v)}/>], [null, null, species]];
      case WorkItemType.TreeGroup:
        return [[height, width, trunk], [trunks, <SelectBox title="Total # of Trees" value={(tree.additionalDetails as Record<string, string>)?.TreeGroup_treeCount}
                                                            options={TREE_COUNT_OPTIONS} onChange={v => setDetail('TreeGroup_treeCount', v)}/>, species]];
      default:
        return [[height, width, trunk], [trunks, null, species]];
    }
  })();
  if (rows.length === 0) return null;
  return (
    <div className="we-measures">
      {rows.flat().map((cell, i) => <div key={i} className="we-measure">{cell}</div>)}
      <datalist id={listId}>{SPECIES_LIST.map(s => <option key={s} value={s}/>)}</datalist>
    </div>
  );
};

// ---- The tray: picture and operation buttons --------------------------------------------------

/** Measuring runs only in the phone apps (AR): a phone or tablet hands off to the app's measuring screen for this work item. */
const useMeasuredPicture = (tree: EstimateTreeDto, onMessage: (text: string) => void) => {
  const navigate = useNavigate();
  return async () => {
    const android = isAndroidDevice();
    const ios = isIosDevice();
    if (!android && !ios) {
      onMessage('Take Measured Picture works on a phone or tablet.');
      return;
    }
    // Android's measure screen signs in with a Firebase custom token of its own
    // (signInWithCustomToken); the iPhone app keeps its own session, so it gets none.
    const token = android ? await getCustomToken(navigate) : undefined;
    const base = android ? process.env.REACT_APP_ANDROID_APP_BASE_DEEP_LINK : process.env.REACT_APP_IOS_APP_BASE_DEEP_LINK;
    window.location.replace(`${base}/measure-tree?${token ? `token=${token}&` : ''}estimate-id=${tree.estimateId}&tree-id=${tree.id}`);
  };
};

/** "A", "A or B", "A, B or C". */
const orList = (names: string[]) => names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;

const Tray = ({tree, items, companyName, onMessage}: {tree: EstimateTreeDto; items: WorkItems; companyName?: string; onMessage: (t: string) => void}) => {
  const [open, setOpen] = useState(true);
  const fileInput = useRef<HTMLInputElement>(null);
  const {settings} = useUserSettings();
  const measured = [WorkItemType.Tree, WorkItemType.TreeGroup, WorkItemType.FallenTree].includes(tree.workItemType as never);
  const addingTypes = items.state.addingOperations[tree.id] ?? [];
  const failed = items.state.failedOperations[tree.id] ?? [];
  const takeMeasured = useMeasuredPicture(tree, onMessage);
  const pick = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const ready = await Promise.all(Array.from(files).map(f => compressImage(f, settings.imageUploadQuality).catch(() => f)));
    void items.uploadPhotos(tree.id, ready);
  };
  return (
    <div className="we-tray">
      <button type="button" className="we-tray-head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>Picture and Operations Buttons</span>{open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
      </button>
      {open && <>
        <div className="we-tray-buttons">
          <button type="button" className="we-pill" onClick={() => fileInput.current?.click()}>Take Picture</button>
          {measured && <button type="button" className="we-pill" onClick={() => void takeMeasured()}>Take Measured Picture</button>}
          <input ref={fileInput} type="file" accept="image/*" multiple hidden
                 onChange={e => { void pick(e.target.files); e.target.value = ''; }}/>
        </div>
        <div className="we-tray-title"><b>Operations</b><i>(select what you are doing)</i></div>
        <div className="we-op-chips">
          {operationsFor(tree.workItemType).map(type => {
            const count = tree.operations.filter(o => o.operationType === type).length;
            const adding = addingTypes.includes(type);
            return (
              <button type="button" key={type} className="we-op-chip" disabled={adding} aria-busy={adding}
                      onClick={() => void items.addOperation(tree.id, type,
                        type === TreeOperationType.Stump_Grinding && companyName ? stumpGrindingSeedNote(companyName) : undefined)}>
                {operationLabel(type).replace('/', '/​')}
                {adding && <CircularProgress size={12} thickness={5} color="inherit" className="we-op-spinner" aria-label="Adding"/>}
                {count > 0 && <span className="we-op-count">{count}</span>}
              </button>
            );
          })}
        </div>
        {failed.length > 0 && <div className="we-op-failed" role="alert">Couldn't add {orList(failed.map(operationLabel))}.</div>}
      </>}
    </div>
  );
};

// ---- Operation cards -----------------------------------------------------------------------

const areaFeet = Array.from({length: 51}, (_, i) => i);

/**
 * OperationAreaPanel (OperationAreaFields.kt): a picked area rises into its
 * own row with its feet (Current and Target, or one Reduction Amount, 0 to 50
 * ft, starting at 0), outline gone and a rule under it; the areas still to
 * pick stay below as outlined blocks with an empty checkbox, the last one the
 * "Add Custom" field.
 */
const Areas = ({op, onPatch}: {op: EstimateTreeOperationDto; onPatch: (patch: Record<string, never>) => void}) => {
  const spec = areaSpecFor(op.operationType)!;
  const entries = areaEntries(spec, op.details as never);
  const [open, setOpen] = useState(true);
  const commit = (next: AreaEntry[]) => onPatch(areaDetailPatches(spec, next) as Record<string, never>);
  const zero = Object.fromEntries(spec.valueFields.map(f => [f.key, 0]));
  const customIndex = entries.findIndex(e => !spec.areas.includes(e.area));
  const unpicked = spec.areas.filter(a => !entries.some(e => e.area === a));
  const picked = [...entries.filter(e => spec.areas.includes(e.area)), ...(customIndex >= 0 ? [entries[customIndex]] : [])];
  const update = (entry: AreaEntry, change: Partial<AreaEntry>) => commit(entries.map(e => e === entry ? {...e, ...change} : e));
  return (
    <div className="we-areas">
      <button type="button" className="we-areas-head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>{spec.title}</span>{open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
      </button>
      {picked.map((entry, i) => {
        const custom = !spec.areas.includes(entry.area);
        return (
          <div key={custom ? `custom-${i}` : entry.area} className="we-area-picked">
            <span className="we-area-name">
              <button type="button" className="we-area-uncheck" onClick={() => commit(entries.filter(e => e !== entry))}
                      aria-label={`Remove ${entry.area || 'custom area'}`}><GreenCheckbox checked/></button>
              {custom
                ? <input className="we-area-custom" value={entry.area} placeholder="Add Custom" onChange={e => update(entry, {area: e.target.value})}/>
                : <span>{entry.area}</span>}
            </span>
            <span className="we-area-values">
              {spec.valueFields.map(field => (
                <label key={field.key} className="we-area-field">
                  <span className="we-area-field-title">{AREA_LABELS[field.key] ?? field.label}</span>
                  <select value={entry.feet[field.key] ?? 0}
                          onChange={e => update(entry, {feet: {...entry.feet, [field.key]: Number(e.target.value)}})}>
                    {areaFeet.map(n => <option key={n} value={n}>{n} ft</option>)}
                  </select>
                </label>
              ))}
            </span>
          </div>
        );
      })}
      {open && (
        <div className="we-area-grid">
          {unpicked.map(area => (
            <button type="button" key={area} className="we-area-block" onClick={() => commit([...entries, {area, feet: zero}])}>
              <GreenCheckbox checked={false}/><span>{area}</span>
            </button>
          ))}
          {customIndex < 0 && (
            <span className="we-area-block we-area-block--custom">
              <button type="button" className="we-area-uncheck" onClick={() => commit([...entries, {area: '', feet: zero}])} aria-label="Add a custom area">
                <GreenCheckbox checked={false}/>
              </button>
              {/* The first keystroke creates the area carrying that text, so nothing typed is lost to the row moving up. */}
              <input className="we-area-custom" value="" placeholder="Add Custom" onChange={e => commit([...entries, {area: e.target.value, feet: zero}])}/>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const OperationFields = ({op, onPatch}: {op: EstimateTreeOperationDto; onPatch: (patch: Record<string, never>) => void}) => {
  if (areaSpecFor(op.operationType)) return <Areas op={op} onPatch={onPatch}/>;
  const details = (op.details ?? {}) as Record<string, unknown>;
  const fields = operationFields(op.operationType);
  const set = (name: string, value: string | number) => onPatch({[name]: value} as Record<string, never>);
  const selects = fields.filter(f => f.kind === 'select');
  return <>
    {fields.filter(f => f.kind === 'pick').map(f => (
      <PickOneRow key={f.name} field={f} value={storedAnswer(f.name, details[f.name])} onPick={v => set(f.name, v)}/>
    ))}
    {selects.length > 0 && (
      <div className="we-stack">
        {selects.map(f => {
          const stored = storedAnswer(f.name, details[f.name]);
          const value = f.numeric ? (stored && Number(stored) > 0 ? `${stored} in` : '') : stored;
          return <SelectBox key={f.name} title={f.label} value={value} options={f.options ?? []}
                            onChange={v => set(f.name, f.numeric ? (v ? Number(v.replace(/\D+$/, '').trim()) : 0) : v)}/>;
        })}
      </div>
    )}
    {fields.filter(f => f.kind === 'text').map(f => (
      <TextBox key={f.name} multiline title={f.label} value={storedAnswer(f.name, details[f.name]) ?? ''} onChange={v => set(f.name, v)}/>
    ))}
  </>;
};

const OperationEdit = ({tree, op, ctx, items}: {tree: EstimateTreeDto; op: EstimateTreeOperationDto; ctx: RecordContext; items: WorkItems}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const minutes = ctx.operationMinutes.get(op.id) ?? 0;
  const aiMinutes = op.aiEstimatedMinutes;
  const aiSelected = aiMinutes != null && op.estimatedMinutesOverride == null;
  const showAi = ctx.aiPricing && (ctx.aiRequested || aiSelected);
  const label = operationLabel(op.operationType);
  return (
    <div className="er-op we-op">
      <div className="we-op-head">
        <span className="er-op-title">{label}</span>
        <button type="button" className="we-trash" onClick={() => setConfirmDelete(true)} aria-label="Delete Operation"><DeleteIcon/></button>
      </div>
      <OperationFields op={op} onPatch={patch => items.changeOperationDetails(tree.id, op.id, patch)}/>
      {hasSelectionList(op.operationType) && <div className="er-op-rule"/>}
      <NoteButton label="Add a Note" placeholder="Add a note for this operation..." value={op.notes}
                  onChange={v => items.changeOperationNotes(tree.id, op.id, v)}/>
      {/* SQ-636: no time, price or stepper where they were hidden from the person. */}
      {!ctx.pricesHidden && <>
        <div className="er-op-rule"/>
        {showAi && <>
          <AiStatus op={op}/>
          <div className="er-price-row">
            <button type="button" className={aiSelected ? 'er-radio er-radio--on' : 'er-radio'} disabled={aiMinutes == null}
                    onClick={() => void items.chooseAi(tree.id, op.id)} aria-label="Use AI Time & Price" aria-pressed={aiSelected}/>
            <span className="er-price-label">AI Time &amp; Price</span>
            <span className="er-price-time">{aiMinutes != null ? formatMinutes(aiMinutes / Math.max(1, ctx.crewSize)) : '—'}</span>
            <span className="er-price-value">{money(op.aiEstimatedCost)}</span>
          </div>
        </>}
        <div className="er-price-row">
          {showAi && <button type="button" className={aiSelected ? 'er-radio' : 'er-radio er-radio--on'}
                             onClick={() => items.stepOperation(tree.id, op.id, 0)} aria-label="Use Your Time & Price" aria-pressed={!aiSelected}/>}
          <span className="er-price-label">Your Time &amp; Price</span>
          {/* SQ-660: with no crew the time divides by nobody: a dash, as on Android (the price stands). */}
          <span className="er-price-time">{ctx.crewSelected && (minutes > 0 || op.estimatedMinutes != null) ? formatMinutes(minutes) : '—'}</span>
          <span className="er-price-value">{money(op.estimatedCost)}</span>
        </div>
        <TimerPill crewSelected={ctx.crewSelected} caption={crewCaption(ctx.crewSize)} target={label} onSelectCrew={ctx.onSelectCrew}
                   onStep={delta => items.stepOperation(tree.id, op.id, delta)}/>
      </>}
      {confirmDelete && (
        <ConfirmDialog title={`Delete ${label}?`} text="This removes the operation and its pricing from the work item." confirm="Delete"
                       onConfirm={() => void items.deleteOperation(tree.id, op.id)} onClose={() => setConfirmDelete(false)}/>
      )}
    </div>
  );
};

// ---- Work Plan -----------------------------------------------------------------------------------

/** The job is being finished (SQ-597): every Work Plan section starts closed, its answer showing. */
const PlanReview = createContext(false);

/**
 * WPSection (WorkPlanBlock.kt): the title in capitals with a chevron; closed,
 * the answer takes the title's place (or the title stays with a grey "No
 * selection"), with any detail lines under it. Open while unanswered.
 */
const Section = ({title, summary, details = [], open, onToggle, children}: {
  title: string; summary?: string; details?: string[]; open: boolean; onToggle: () => void; children: ReactNode;
}) => (
  <div className="we-wp-section">
    <button type="button" className="we-wp-title" onClick={onToggle} aria-expanded={open}>
      {!open && summary
        ? <span className="we-wp-summary">{summary}</span>
        : <><span className="we-wp-name">{title}</span>{!open && <span className="we-wp-none">No selection</span>}</>}
      {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
    </button>
    {!open && details.length > 0 && <div className="we-wp-details">{details.map(line => <div key={line}>{line}</div>)}</div>}
    {open && <div className="we-wp-content">{children}</div>}
  </div>
);

/** WPPickSection: one wrapping row of chips; a pick closes it, re-tapping clears it and keeps it open. */
const PlanPick = ({title, options, value, answer = v => v, labels, row, onPick}: {
  title: string; options: string[]; value: string; answer?: (v: string) => string; labels?: Record<string, string>; row?: boolean;
  onPick: (v: string) => void;
}) => {
  const review = useContext(PlanReview);
  const [open, setOpen] = useState(!review && !value);
  // An answer cleared from elsewhere (a Cleanup change, SQ-644) asks again.
  useEffect(() => { if (!review && !value) setOpen(true); }, [review, value]);
  return (
    <Section title={title} summary={value ? answer(value) : undefined} open={open} onToggle={() => setOpen(o => !o)}>
      <PickOne options={options} value={value} labels={labels} row={row} onPick={v => { onPick(v); setOpen(!v); }}/>
    </Section>
  );
};

/** One − value + group (feet and inches are independent: 16 in stays 0 ft 16 in). */
const Steps = ({value, unit, min = 44, onChange}: {value: number; unit: string; min?: number; onChange: (v: number) => void}) => (
  <span className="we-steps">
    <StepButton label={`Less, ${unit}`} glyph="−" onStep={() => onChange(Math.max(0, value - 1))}/>
    <b style={{minWidth: min}}>{value} {unit}</b>
    <StepButton label={`More, ${unit}`} glyph="+" onStep={() => onChange(value + 1)}/>
  </span>
);

/**
 * SpecBlock: a hairline and the title in capitals, then (when asked) the
 * diameter stepper, the length chips (and Cut Length in ft and in), and the
 * location chips (and the note). `spec` null: the diameter only.
 */
const SpecBlock = ({title, sizeLabel, diameter, onDiameter, spec, locationOnly, onSpec}: {
  title: string; sizeLabel?: string; diameter?: number; onDiameter?: (v: number) => void;
  spec: TreeMaterialSpec | null; locationOnly?: boolean; onSpec?: (s: TreeMaterialSpec) => void;
}) => {
  const set = (patch: Partial<TreeMaterialSpec>) => spec && onSpec?.({...spec, ...patch});
  return (
    <div className="we-spec">
      <div className="we-spec-title">{title}</div>
      {sizeLabel && onDiameter && <div className="we-spec-row"><span>{sizeLabel}</span><Steps value={diameter ?? 0} unit="in" onChange={onDiameter}/></div>}
      {spec && !locationOnly && <PickOne grid options={[ANY_LENGTH, CUT_TO_LENGTH]} value={spec.lengthMode} onPick={v => set({lengthMode: v})}/>}
      {spec && !locationOnly && spec.lengthMode === CUT_TO_LENGTH && (
        <div className="we-spec-row"><span>Cut Length</span>
          <span className="we-steps-pair">
            <Steps value={spec.lengthFt} unit="ft" min={38} onChange={v => set({lengthFt: v})}/>
            <Steps value={spec.lengthIn} unit="in" min={38} onChange={v => set({lengthIn: v})}/>
          </span>
        </div>
      )}
      {spec && <PickOne grid options={[WHERE_IT_FALLS, SPECIFIC_LOCATION]} value={spec.location} onPick={v => set({location: v})}/>}
      {spec && spec.location === SPECIFIC_LOCATION && (
        <textarea className="we-note-field we-spec-note" rows={2} autoCapitalize="sentences" value={spec.locationNote} placeholder="Where should it go?"
                  onChange={e => set({locationNote: e.target.value})}/>
      )}
    </div>
  );
};

/**
 * BrushAndWoodSection: a "leave" answer rises into a green-outlined group
 * with its questions and a Collapse link; the other answers stay below so the
 * estimator can switch. Clean/Haul has no questions and closes the section.
 * On a Misc. item it is Cleanup with two answers and only the location.
 */
const BrushAndWood = ({tree, misc, items}: {tree: EstimateTreeDto; misc: boolean; items: WorkItems}) => {
  const answer = detail(tree, 'brushWood');
  const wood = specOf(tree, 'woodSpec');
  const brush = specOf(tree, 'brushSpec');
  const haul = Number(detail(tree, 'haulWoodDiameter') || 0);
  const review = useContext(PlanReview);
  const [open, setOpen] = useState(!review && !brushWoodComplete(answer, wood, brush, misc));
  const set = (values: Partial<TreeAdditionalDetails>) => items.setDetails(tree.id, values);
  const pick = (option: string) => {
    const clearing = answer === option;
    set({brushWood: clearing ? '' : option});
    setOpen(clearing || option !== CLEAN_HAUL);
  };
  const inGroup = !!answer && answer !== CLEAN_HAUL;
  return (
    <Section title={misc ? 'Cleanup' : 'Brush and Wood'} summary={answer || undefined} details={brushWoodLines(answer, haul, wood, brush, misc)}
             open={open} onToggle={() => setOpen(o => !o)}>
      {inGroup && (
        <div className="we-plan-group">
          <button type="button" className="we-plan-picked" onClick={() => pick(answer)}><span className="we-radio we-radio--on"/>{answer}</button>
          {answer === LEAVE_EVERYTHING && misc && <SpecBlock title="Left On Site" spec={wood} locationOnly onSpec={s => set({woodSpec: s})}/>}
          {answer === LEAVE_EVERYTHING && !misc && <>
            <SpecBlock title="Wood Left On Site" spec={wood} onSpec={s => set({woodSpec: s})}/>
            <SpecBlock title="Brush Left On Site" spec={brush} onSpec={s => set({brushSpec: s})}/>
          </>}
          {answer === LEAVE_BRUSH_HAUL_WOOD && <>
            <SpecBlock title="Wood Hauled Off" sizeLabel="Haul off wood this size and bigger" diameter={haul} spec={null}
                       onDiameter={v => set({haulWoodDiameter: v})}/>
            <SpecBlock title="Brush Left On Site" spec={brush} onSpec={s => set({brushSpec: s})}/>
          </>}
          {answer === LEAVE_WOOD_HAUL_BRUSH && (
            <SpecBlock title="Wood Left On Site" sizeLabel="Leave wood this size and bigger" diameter={wood.diameter} spec={wood}
                       onDiameter={v => set({woodSpec: {...wood, diameter: v}})} onSpec={s => set({woodSpec: s})}/>
          )}
          <div className="we-plan-collapse"><button type="button" onClick={() => setOpen(false)}>Collapse</button></div>
        </div>
      )}
      <div className="we-chips we-chips--grid">
        {brushWoodOptions(misc).filter(o => !inGroup || o !== answer).map(o => <Chip key={o} label={o} on={!inGroup && o === answer} onClick={() => pick(o)}/>)}
      </div>
    </Section>
  );
};

/** The stump's Cleanup: the same shape, the follow-up asking where the grindings or the excess go. */
const StumpCleanup = ({tree, items}: {tree: EstimateTreeDto; items: WorkItems}) => {
  const answer = detail(tree, 'cleanup');
  const where = detail(tree, 'cleanupLocation');
  const note = detail(tree, 'cleanupNote');
  const review = useContext(PlanReview);
  const [open, setOpen] = useState(!review && !cleanupComplete(answer, where, note));
  const follow = answer ? CLEANUP_FOLLOW_UP[answer] : undefined;
  const pick = (option: string) => {
    const clearing = answer === option;
    items.setDetails(tree.id, cleanupPatch(tree, clearing ? '' : option));
    setOpen(clearing || !!CLEANUP_FOLLOW_UP[option]);
  };
  const line = follow ? cleanupDetail(where, note) : undefined;
  return (
    <Section title="Cleanup" summary={answer || undefined} details={line ? [line] : []} open={open} onToggle={() => setOpen(o => !o)}>
      {follow && (
        <div className="we-plan-group">
          <button type="button" className="we-plan-picked" onClick={() => pick(answer)}><span className="we-radio we-radio--on"/>{answer}</button>
          <div className="we-spec">
            <div className="we-spec-title">{follow}</div>
            <PickOne grid options={[HAULED_AWAY, SPECIFIC_LOCATION]} value={where} onPick={v => items.setDetails(tree.id, {cleanupLocation: v})}/>
            {where === SPECIFIC_LOCATION && (
              <textarea className="we-note-field we-spec-note" rows={2} autoCapitalize="sentences" value={note} placeholder="Where should it go?"
                        onChange={e => items.setDetails(tree.id, {cleanupNote: e.target.value})}/>
            )}
          </div>
          <div className="we-plan-collapse"><button type="button" onClick={() => setOpen(false)}>Collapse</button></div>
        </div>
      )}
      <div className="we-chips we-chips--grid">
        {CLEANUP_OPTIONS.filter(o => !follow || o !== answer).map(o => <Chip key={o} label={o} on={!follow && o === answer} onClick={() => pick(o)}/>)}
      </div>
    </Section>
  );
};

/** The machines on the item (WorkPlanBlock.kt EquipmentSection): closed to start, one open at a time, the company's others below to pick. */
const Equipment = ({tree, items, minimumMachines}: {tree: EstimateTreeDto; items: WorkItems; minimumMachines?: Set<number>}) => {
  const machines = planMachines(tree, items.state.companyEquipment, tree.equipment ?? []);
  const [openKey, setOpenKey] = useState<string>();
  if (machines.length === 0) {
    return <div className="we-equipment er-small er-muted">No equipment configured for the company.</div>;
  }
  const picked = pickedInOrder(tree, machines);
  const stump = isStumpWork(tree);
  const materialOptions = loaderMaterialOptions(tree);
  const materialLabels = Object.fromEntries(materialOptions.map(o => [o, loaderMaterialLabel(tree, o)]));
  const unpicked = machines.filter(m => !m.picked);
  // SQ-636: where the record's prices were hidden from the person, a machine is its name alone: no billed
  // time, price or stepper, and none to pick (a pick locks a rate onto the price).
  if (items.state.pricesMasked === true) {
    return picked.length === 0 ? null : (
      <div className="we-equipment">
        {picked.map(item => (
          <div key={item.key} className="we-machine">
            <div className="we-machine-head">
              <span className="we-machine-name">{item.row ? equipmentName(item.row) : item.machine.nickname || item.machine.type}</span>
            </div>
            {isCompactLoader(item.machine, item.row) && (detail(tree, 'loaderMaterial') || detail(tree, 'loaderAccess')) && (
              <div className="we-wp-details">
                {detail(tree, 'loaderMaterial') && <div>{loaderMaterialAnswer(loaderMaterialLabel(tree, detail(tree, 'loaderMaterial')))}</div>}
                {detail(tree, 'loaderAccess') && <div>{loaderAccessAnswer(detail(tree, 'loaderAccess'))}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  const setChecked = (key: string, equipmentId: number, checked: boolean) => {
    // SQ-636: refused before the order is saved where the prices are hidden (the row's write would be).
    if (items.refusesTimeAndPrice()) return;
    const order = storedOrder(tree);
    items.setDetails(tree.id, {workPlanEquipment: checked ? appendKey(order, key) : removeKey(order, key)});
    // A pick creates the row at 0 minutes, which locks today's rate onto the estimate; an unpick removes it.
    if (checked) items.setEquipmentMinutes(tree.id, equipmentId, 0);
    else items.removeEquipment(tree.id, equipmentId);
  };
  return (
    <div className="we-equipment">
      {picked.map(item => {
        const isOpen = openKey === item.key;
        const loader = isCompactLoader(item.machine, item.row);
        const minutes = item.row?.durationMinutes ?? 0;
        const material = detail(tree, 'loaderMaterial');
        const access = detail(tree, 'loaderAccess');
        return (
          <div key={item.key} className="we-machine">
            <div className="we-machine-head">
              {isOpen && (
                <button type="button" className="we-area-uncheck" aria-label={`Remove ${item.row ? equipmentName(item.row) : item.machine.nickname}`}
                        onClick={() => { setOpenKey(undefined); setChecked(item.key, item.machine.id, false); }}><GreenCheckbox checked/></button>
              )}
              <button type="button" className="we-machine-toggle" onClick={() => setOpenKey(k => k === item.key ? undefined : item.key)} aria-expanded={isOpen}>
                <span className="we-machine-name">{item.row ? equipmentName(item.row) : item.machine.nickname || item.machine.type}</span>
                <span className="we-machine-time">{formatMinutes(minutes)}</span>
                <span className="we-machine-price">{item.row?.hourlyRate == null ? '—' : money(rowCost(item.row))}</span>
                {isOpen ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
              </button>
            </div>
            {item.row && minimumApplied(item.row, minimumMachines) && <MinimumAppliedTag/>}
            {item.row?.rateChanged && (
              <div className="er-notice">This equipment's rate changed. This estimate keeps its rate; remove and add it again to use the new values.</div>
            )}
            {!isOpen && loader && (material || access) && (
              <div className="we-wp-details">
                {material && <div>{loaderMaterialAnswer(loaderMaterialLabel(tree, material))}</div>}
                {access && <div>{loaderAccessAnswer(access)}</div>}
              </div>
            )}
            {isOpen && (
              <div className="we-machine-body">
                <TimerPill target={item.machine.nickname || item.machine.type} onStep={delta => items.stepEquipment(tree.id, item.machine.id, delta)}/>
                {loader && <>
                  <PlanPick title={stump ? 'Hauling to Truck/Staging' : 'Hauling to Chipper/Staging'} options={materialOptions} value={material} labels={materialLabels}
                            answer={v => loaderMaterialAnswer(loaderMaterialLabel(tree, v))} row={stump}
                            onPick={v => items.setDetails(tree.id, {loaderMaterial: v})}/>
                  <PlanPick title="Machine Access" options={LOADER_ACCESS_OPTIONS} value={access} answer={loaderAccessAnswer}
                            onPick={v => items.setDetails(tree.id, {loaderAccess: v})}/>
                </>}
              </div>
            )}
          </div>
        );
      })}
      {unpicked.length > 0 && (
        <div className="we-machine-menu">
          <div className="we-machine-menu-title">EQUIPMENT</div>
          <div className="we-machine-grid">
            {unpicked.map(item => (
              <button type="button" key={item.key} className="we-machine-pick"
                      onClick={() => { setChecked(item.key, item.machine.id, true); setOpenKey(item.key); }}>
                <GreenCheckbox checked={false}/>{item.machine.nickname || item.machine.type}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * ReviewEquipmentSection (WorkPlanBlock.kt), while the job is finished: each
 * machine's billed time and price stay (only Edit changes them); its stepper
 * moves the time it ACTUALLY ran, and the line under the billed figures says
 * what that comes to. A machine picked here is billed nothing and can be
 * unchecked again; one on the job's price stays, even stepped to zero.
 */
const ReviewEquipment = ({tree, items, judged}: {tree: EstimateTreeDto; items: WorkItems; judged: boolean}) => {
  const machines = planMachines(tree, items.state.companyEquipment, tree.equipment ?? []);
  if (machines.length === 0) {
    return <div className="we-equipment er-small er-muted">No equipment configured for the company.</div>;
  }
  const used = pickedInOrder(tree, machines).filter(item => item.row);
  const unused = machines.filter(m => !m.row);
  const order = storedOrder(tree);
  // SQ-636: where the record's prices were hidden from the person, the machines used are their names alone:
  // no billed or actual time, price or stepper, and none to add.
  if (items.state.pricesMasked === true) {
    return used.length === 0 ? null : (
      <div className="jb-equipment">
        {used.map(item => (
          <div key={item.key} className="jb-machine" role="group" aria-label={equipmentName(item.row!)}>
            <div className="jb-machine-head"><span className="jb-machine-name">{equipmentName(item.row!)}</span></div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="jb-equipment">
      <div className="jb-equipment-intro">Equipment time — change it if a machine ran longer or shorter</div>
      {used.map(item => {
        const row = item.row!;
        const name = equipmentName(row);
        const line = machineRanLine(row, judged);
        return (
          <div key={item.key} className="jb-machine" role="group" aria-label={name}>
            <div className="jb-machine-head">
              {row.durationMinutes <= 0 && (
                <button type="button" className="we-area-uncheck" aria-label={`Remove ${name}`} onClick={() => {
                  if (items.refusesTimeAndPrice()) return;
                  items.removeEquipment(tree.id, item.machine.id);
                  items.setDetails(tree.id, {workPlanEquipment: removeKey(storedOrder(tree), item.key)});
                }}><GreenCheckbox checked/></button>
              )}
              <span className="jb-machine-name">{name}</span>
              <b>{formatMinutes(row.durationMinutes)}</b>
              <b>{row.hourlyRate == null ? '—' : money(rowCost(row))}</b>
            </div>
            {line && <div className={line[1] ? 'jb-machine-line jb-machine-line--over' : 'jb-machine-line'}>{line[0]}</div>}
            <TimerPill target={`${name} actual`} onStep={delta => items.stepEquipmentActual(tree.id, item.machine.id, delta)}/>
          </div>
        );
      })}
      {unused.length > 0 && (
        <div className="we-machine-menu">
          <div className="we-machine-menu-title">EQUIPMENT</div>
          <div className="we-machine-grid">
            {unused.map(item => (
              <button type="button" key={item.key} className="we-machine-pick" onClick={() => {
                if (items.refusesTimeAndPrice()) return;
                // Not on the job's price: added billed at nothing, so all of its time reads as over.
                items.setEquipmentMinutes(tree.id, item.machine.id, 0);
                items.setDetails(tree.id, {workPlanEquipment: appendKey(order, item.key)});
              }}>
                <GreenCheckbox checked={false}/>{item.machine.nickname || item.machine.type}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** [judged]: in review, a machine's actual time is weighed against what it is billed (seesActuals). */
export const WorkPlanEdit = ({tree, items, review = false, judged = true, minimumMachines}: {
  tree: EstimateTreeDto; items: WorkItems; review?: boolean; judged?: boolean; minimumMachines?: Set<number>;
}) => {
  const [open, setOpen] = useState(true);
  const s = planSections(tree);
  if (!s.any) return null;
  const set = (values: Partial<TreeAdditionalDetails>) => items.setDetails(tree.id, values);
  return (
    <div className="we-wp">
      <button type="button" className="we-wp-head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>Work Plan</span><i>(select everything that applies)</i>{open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
      </button>
      {open && (
        <div className="we-wp-body">
          <PlanReview.Provider value={review}>
          {s.cleanup && <StumpCleanup tree={tree} items={items}/>}
          {s.brushWood && <BrushAndWood tree={tree} misc={s.misc} items={items}/>}
          {s.removal && (
            <PlanPick title="Removal Plan" options={REMOVAL_OPTIONS} value={detail(tree, 'removalStrategy')}
                      onPick={v => set(v === FELL_WHOLE_TREE
                        ? {removalStrategy: v, reachMethod: '', debrisMethod: '', riggingAmount: '', remainingStem: ''}
                        : {removalStrategy: v})}/>
          )}
          {s.treeWork && <>
            <PlanPick title="Reach Method" options={REACH_OPTIONS} value={detail(tree, 'reachMethod')} onPick={v => set({reachMethod: v})}/>
            <PlanPick title="Dropping Method" options={DROPPING_OPTIONS} value={detail(tree, 'debrisMethod')} answer={droppingAnswer}
                      onPick={v => set({debrisMethod: v})}/>
            <PlanPick title="Rigging" options={RIGGING_OPTIONS} value={detail(tree, 'riggingAmount')} answer={riggingAnswer}
                      onPick={v => set({riggingAmount: v})}/>
            {s.remainingStem && (
              <PlanPick title="Remaining Stem" options={REMAINING_STEM_OPTIONS} value={detail(tree, 'remainingStem')} onPick={v => set({remainingStem: v})}/>
            )}
          </>}
          {review ? <ReviewEquipment tree={tree} items={items} judged={judged}/> : <Equipment tree={tree} items={items} minimumMachines={minimumMachines}/>}
          </PlanReview.Provider>
        </div>
      )}
    </div>
  );
};

// ---- Notes leaving the page (LegacyNotesBlock) ---------------------------------------------------

/**
 * The work item note and Debris Management left this page (SQ-487 Part 4):
 * a work item that still has text in one shows it, editable, so it can be cut
 * into the operation it belongs to. Emptied and saved, it stops showing.
 */
const LegacyNotes = ({tree, items}: {tree: EstimateTreeDto; items: WorkItems}) => {
  const [hadNote] = useState(!!tree.notes?.trim());
  const [hadDebris] = useState(!!detail(tree, 'debrisManagement'));
  if (!hadNote && !hadDebris) return null;
  return (
    <div className="we-legacy">
      <div className="we-legacy-hint">Move this into the operation it belongs to, then clear this field.</div>
      {hadNote && (
        <label className="we-select"><span className="we-select-title">Work Item Note</span>
          <textarea className="we-note-field we-note-field--crew" autoCapitalize="sentences" rows={noteRows(tree.notes)} value={tree.notes ?? ''}
                    onChange={e => items.changeTree(tree.id, t => ({...t, notes: e.target.value}))}/>
        </label>
      )}
      {hadDebris && (
        <label className="we-select"><span className="we-select-title">Debris Management</span>
          <textarea className="we-note-field we-note-field--crew" autoCapitalize="sentences" rows={noteRows(detail(tree, 'debrisManagement'))} value={detail(tree, 'debrisManagement')}
                    onChange={e => items.setDetails(tree.id, {debrisManagement: e.target.value})}/>
        </label>
      )}
    </div>
  );
};

// ---- The card ----------------------------------------------------------------------------------

const WorkItemEdit = ({tree, number, gates, ctx, items, companyName, onOpenPhoto, onEditPhoto, onHideGatePhoto, onMessage, cardRef, allocation}: {
  tree: EstimateTreeDto; number: number; gates: EstimateGateDetailsDto[]; ctx: RecordContext; items: WorkItems; companyName?: string;
  onOpenPhoto: (photo: Photo) => void; onEditPhoto: (photo: Photo) => void; onMessage: (text: string) => void;
  /** SQ-716: takes a connected gate's photo off this work item only. */
  onHideGatePhoto: (gateId: number, imageId: number) => void;
  cardRef?: (el: HTMLElement | null) => void;
  /** A completed job being corrected (SQ-597): its tan time card, under the total, where the verdict was. */
  allocation?: ReactNode;
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletePhotoId, setDeletePhotoId] = useState<number>();
  const [hideGatePhoto, setHideGatePhoto] = useState<Photo>();
  const photos = photosOf(tree, gates);
  const equipmentCost = (tree.equipment ?? []).reduce((sum, row) => sum + rowCost(row), 0);
  const laborCost = tree.operations.reduce((sum, o) => sum + (o.estimatedCost ?? 0), 0);
  const minutes = ctx.itemMinutes.get(tree.id) ?? 0;
  const uploading = items.state.uploading[tree.id] ?? 0;
  return (
    <article className="er-item we-item" ref={cardRef}>
      <header className="er-item-band">{workItemTitle(tree, number)}</header>
      <div className="er-item-body">
        <PhotoPager photos={photos} onOpen={onOpenPhoto} overlay={photo => photo.imageId !== undefined ? <>
          <button type="button" className="we-photo-delete" onClick={() => setDeletePhotoId(photo.imageId)} aria-label="Delete photo"><DeleteIcon/></button>
          <button type="button" className="we-photo-edit" onClick={() => onEditPhoto(photo)}>Edit Picture</button>
        </> : photo.gateId !== undefined && (
          // A gate's photo, as Android's: removed from this work item only (SQ-716).
          <button type="button" className="we-photo-delete" onClick={() => setHideGatePhoto(photo)} aria-label="Delete photo"><DeleteIcon/></button>
        )}/>
        {uploading > 0 && <div className="er-small er-muted we-uploading">Uploading photos…</div>}
        {items.state.uploadFailed[tree.id] && <div className="er-small er-error">Photo upload failed — add the picture again to retry.</div>}
        <SiteLine tree={tree} gates={gates}/>
        <Measurements tree={tree} items={items}/>
        <Tray tree={tree} items={items} companyName={companyName} onMessage={onMessage}/>
        {tree.operations.length === 0
          ? <div className="er-small er-muted er-none">No operations yet.</div>
          : tree.operations.map(op => <OperationEdit key={op.id} tree={tree} op={op} ctx={ctx} items={items}/>)}
        <WorkPlanEdit tree={tree} items={items} minimumMachines={ctx.minimumMachines}/>
        <div className="er-item-rule"/>
        <LegacyNotes tree={tree} items={items}/>
        <NoteButton crew label="Add a Crew Note" title="Crew Note" placeholder="Add a note for the crew..." value={tree.crewNotes}
                    onChange={v => items.changeTree(tree.id, t => ({...t, crewNotes: v}))}/>
        {/* SQ-636: no totals or stepper where the prices were hidden from the person. */}
        {!ctx.pricesHidden && <>
          <div className="er-item-rule er-item-rule--totals"/>
          <div className="er-total-row"><span>Equipment Cost</span><span className="er-total-value">{money(equipmentCost)}</span></div>
          <div className="er-total-row">
            <span>Labor Time &amp; Cost</span>
            <span className="er-total-value">{ctx.crewSelected ? formatMinutes(minutes) : '—'}</span>
            <span className="er-total-value">{money(laborCost)}</span>
          </div>
          {/* SQ-660: the timer spreads by price, so every operation needs one first. */}
          {tree.operations.length > 0
            ? <TimerPill crewSelected={ctx.crewSelected} caption={crewCaption(ctx.crewSize)} target="Item Total" onSelectCrew={ctx.onSelectCrew}
                         blockedReason={missingPriceReason([tree], [tree], true, 'time')}
                         stepRefusal={delta => parentStepRefusal([tree], [tree], true, delta, ctx.crewSize, ctx.rate)}
                         onStep={delta => items.stepTree(tree.id, delta)}/>
            : ctx.crewSelected && minutes > 0 && <div className="er-crew-caption">{crewCaption(ctx.crewSize)}</div>}
          <div className="er-total-row er-total-row--strong">
            <span>{totalLabel(tree.workItemType)}</span>
            <span className="er-total-value">{money(equipmentCost + laborCost)}</span>
          </div>
        </>}
        {allocation}
        <div className="er-item-rule"/>
        <button type="button" className="we-delete-whole" onClick={() => setConfirmDelete(true)}>Delete Whole {deleteWholeLabel(tree.workItemType)}</button>
      </div>
      {confirmDelete && (
        <ConfirmDialog title="Delete whole tree?" text="This removes the work item and all of its operations." confirm="Delete"
                       onConfirm={() => void items.deleteWorkItem(tree.id)} onClose={() => setConfirmDelete(false)}/>
      )}
      {hideGatePhoto?.gateId !== undefined && hideGatePhoto.gateImageId !== undefined && (
        <ConfirmDialog {...hideGatePhotoConfirm(hideGatePhoto.gate)}
                       onConfirm={() => onHideGatePhoto(hideGatePhoto.gateId!, hideGatePhoto.gateImageId!)} onClose={() => setHideGatePhoto(undefined)}/>
      )}
      {deletePhotoId !== undefined && (
        <ConfirmDialog title="Delete photo?" text="This removes the photo from the work item." confirm="Delete"
                       onConfirm={() => void items.deletePhoto(tree.id, deletePhotoId)} onClose={() => setDeletePhotoId(undefined)}/>
      )}
    </article>
  );
};

export default WorkItemEdit;
